import React from "react";
import { useTranslation } from "react-i18next";
import Modal from "components/Modal";
import Button from "components/Button";
import { IconClose } from "icons";
import paymentService from "api/payment/payment.service";
import userService from "api/user/user.service";
import useAuthToken from "hooks/useAuthToken";
import { useAppSelector } from "store";
import { AnalyticsEvent, formatDate, DateFormat } from "utils";
import { isScheduledChangeRefusal } from "hooks/usePaymentActionHandler";
import CreditShortfallInfo from "./CreditShortfallInfo";

interface EndTrialModalProps {
  onClose: () => void;
  requiredCredits?: number | null;
  remainingCredits?: number | null;
}

const EndTrialModal: React.FC<EndTrialModalProps> = ({ onClose, requiredCredits = null, remainingCredits = null }) => {
  const { t } = useTranslation();
  const { user } = useAppSelector((state) => state.common);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleEndTrial = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await paymentService.endTrial();
      // The route answers a refusal with HTTP 200 and a bare `{action: "error", reason_code}` (legacy's shape) — not a
      // success: no token refresh, no success event, the modal stays open and says why.
      const refusal = res?.responseData?.action === "error" ? res.responseData : null;

      if (refusal) {
        setError(
          isScheduledChangeRefusal(refusal) && refusal.scheduled_at
            ? t("billing:payment_action.change_already_scheduled", { date: formatDate(refusal.scheduled_at, DateFormat.SHORT) })
            : t("billing:end_trial_modal.error_generic")
        );

        return;
      }

      if (user.email) {
        const tokenRes = await userService.refreshToken({ email: user.email });
        useAuthToken.setAuthTokenFromLocalStorage(tokenRes.responseData.token);
      }

      AnalyticsEvent("End_Trial", "", "success");
      onClose();
    } catch {
      setError(t("billing:end_trial_modal.error_generic"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose} show={true} bodyClassName="!border !border-grey-light bg-trueWhite dark:bg-surface-dark !max-w-[480px] !w-full p-5" className="!z-[99999]" backdropDisabled={true}>
      <div className="relative flex flex-col items-center gap-6 w-full">
        <div className="cursor-pointer absolute top-0 right-0 w-fit bg-grey-light rounded-full p-0.5" onClick={onClose}>
          <IconClose />
        </div>

        <div className="flex flex-col items-center gap-2.5 mt-4">
          <h3 className="text-h3 text-center dark:text-white">{t("billing:end_trial_modal.title")}</h3>
          <span className="text-bodyMd text-center text-grey">{t("billing:end_trial_modal.description")}</span>
        </div>

        <CreditShortfallInfo requiredCredits={requiredCredits} remainingCredits={remainingCredits} />

        <div className="flex flex-col gap-2 w-full bg-grey-light/30 dark:bg-black-300/50 rounded-lg p-4">
          <span className="text-bodySm text-grey text-center">{t("billing:end_trial_modal.footnote_charge")}</span>
          <span className="text-bodySm text-grey text-center">{t("billing:end_trial_modal.footnote_credits")}</span>
        </div>

        {error && <span className="text-bodySm text-orange text-center bg-orange/10 rounded-md px-3 py-2 w-full">{error}</span>}

        <Button className="btn-primary w-full" onClick={handleEndTrial} disabled={loading}>
          {loading ? t("billing:end_trial_modal.cta_loading") : t("billing:end_trial_modal.cta")}
        </Button>
      </div>
    </Modal>
  );
};

export default EndTrialModal;
